/**
 * ImageUploadForm Component
 *
 * A reusable component for drag-and-drop uploads with an upload button.
 * Accepts images by default; pass `accept` to allow PDFs as well.
 * Handles file selection, drag-and-drop, and triggers upload via mutation hook.
 */

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { AlertCircle } from 'lucide-react';
import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import {
  MAX_FILE_SIZE,
  useUploadImagesToOrgMutation,
} from '../../hooks/useCloudinaryUpload';
import { cn } from '../../utils/cn';
import {
  ACCEPT_CONFIG,
  isAcceptedFileType,
  type FileAccept,
} from '../../utils/fileTypes';
import AssetTagInput, { type AssetTagValue } from '../AssetTagInput';

export interface ImageUploadFormProps {
  /** Optional input ID for file input (for unique IDs when multiple instances) */
  inputId?: string;
  /** Callback when upload is successful – receives the API response (array of created assets) */
  onUploadSuccess?: (result?: any) => void;
  /** Optional orgId - if not provided, will try to get from user context */
  orgId?: string;
  /** Whether to allow multiple file uploads. Defaults to true. */
  multiple?: boolean;
  /** Whether to show the tag input. Defaults to true. */
  showTags?: boolean;
  /** Which kinds of files to accept. Defaults to 'image'. */
  accept?: FileAccept;
}

/** Format bytes to human-readable string */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface RejectedFile {
  file: File;
  error: string;
}

export default function ImageUploadForm({
  inputId = 'image-upload-input',
  onUploadSuccess,
  orgId: providedOrgId,
  multiple = true,
  showTags = true,
  accept = 'image',
}: ImageUploadFormProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedFile[]>([]);
  const [tags, setTags] = useState<AssetTagValue[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptConfig = ACCEPT_CONFIG[accept];

  /** Noun for the accepted file kind, pluralised for `count` items. */
  const fileNoun = (count: number) =>
    count === 1 ? acceptConfig.noun : acceptConfig.nounPlural;

  /** Same noun, capitalised for use at the start of a sentence. */
  const fileNounSentence = (count: number) => {
    const noun = fileNoun(count);
    return noun.charAt(0).toUpperCase() + noun.slice(1);
  };

  // Get orgId from prop or user context
  const orgId = providedOrgId || '';

  const { mutateAsync: uploadImages, isPending } = useUploadImagesToOrgMutation(
    {
      onSuccess: (result) => {
        setSelectedFiles([]);
        onUploadSuccess?.(result);
        setTags([]);
      },
    },
  );

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const validateFiles = (
    files: File[],
  ): { valid: File[]; errors: Map<string, string> } => {
    const valid: File[] = [];
    const errors = new Map<string, string>();

    for (const file of files) {
      if (!isAcceptedFileType(file, accept)) {
        errors.set(file.name, acceptConfig.errorMessage);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        errors.set(
          file.name,
          `File size (${formatFileSize(file.size)}) exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`,
        );
        continue;
      }

      valid.push(file);
    }

    return { valid, errors };
  };

  const handleFileSelect = (fileList: FileList) => {
    const files = Array.from(fileList);
    const { valid, errors } = validateFiles(files);

    // Track rejected files with their errors
    if (errors.size > 0) {
      const rejected: RejectedFile[] = [];
      errors.forEach((error, fileName) => {
        const file = files.find((f) => f.name === fileName);
        if (file) {
          rejected.push({ file, error });
        }
      });
      setRejectedFiles((prev) => [...prev, ...rejected]);

      // Show toast notification
      const oversizedFiles = Array.from(errors.entries()).filter(([_, error]) =>
        error.includes('exceeds maximum'),
      );
      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map(([name]) => name).join(', ');
        toastError(
          `${fileNounSentence(oversizedFiles.length)} too large: ${fileNames}. Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}.`,
        );
      }
    }

    if (valid.length > 0) {
      if (multiple) {
        setSelectedFiles((prev) => [...prev, ...valid]);
      } else {
        setSelectedFiles([valid[0]]);
      }
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files || files.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    handleFileSelect(files);

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      return;
    }

    // Double-check file sizes before upload (in case files were added before validation was added)
    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > MAX_FILE_SIZE,
    );
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => file.name).join(', ');
      toastError(
        `Cannot upload: ${fileNounSentence(oversizedFiles.length)} too large (${fileNames}). Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}.`,
      );
      return;
    }

    try {
      await uploadImages({
        orgId,
        files: selectedFiles,
        assetTags: tags.length > 0 ? tags : undefined,
      });
      toastSuccess(
        `${fileNounSentence(selectedFiles.length)} uploaded successfully!`,
      );
    } catch (error: any) {
      console.error('Error uploading files:', error);
      const errorMessage =
        error?.message ||
        `Failed to upload ${acceptConfig.nounPlural}. Please try again.`;
      toastError(errorMessage);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
    setRejectedFiles([]);
    setTags([]);
  };

  const handleClearRejected = () => {
    setRejectedFiles([]);
  };

  // Get oversized files from rejected files
  const oversizedRejectedFiles = rejectedFiles.filter((rf) =>
    rf.error.includes('exceeds maximum'),
  );

  return (
    <div className="space-y-4">
      {/* Error Alert for oversized files */}
      {oversizedRejectedFiles.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>File size error</AlertTitle>
          <AlertDescription>
            <div className="mt-2 space-y-1">
              <p>
                {oversizedRejectedFiles.length === 1
                  ? `The following ${acceptConfig.noun} is too large to upload:`
                  : `The following ${acceptConfig.nounPlural} are too large to upload:`}
              </p>
              <ul className="ml-4 list-disc space-y-1">
                {oversizedRejectedFiles.map((rf, index) => (
                  <li key={`${rf.file.name}-${index}`} className="text-sm">
                    <span className="font-medium">{rf.file.name}</span> (
                    {formatFileSize(rf.file.size)})
                  </li>
                ))}
              </ul>
              <p className="mt-2 font-medium">
                Maximum file size: {formatFileSize(MAX_FILE_SIZE)}
              </p>
              <button
                type="button"
                onClick={handleClearRejected}
                className="mt-2 text-sm underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Other rejected files (non-size errors) */}
      {rejectedFiles.filter((rf) => !rf.error.includes('exceeds maximum'))
        .length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Some files could not be added</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 ml-4 list-disc space-y-1">
              {rejectedFiles
                .filter((rf) => !rf.error.includes('exceeds maximum'))
                .map((rf, index) => (
                  <li key={`${rf.file.name}-${index}`} className="text-sm">
                    <span className="font-medium">{rf.file.name}</span>:{' '}
                    {rf.error}
                  </li>
                ))}
            </ul>
            <button
              type="button"
              onClick={handleClearRejected}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Dismiss
            </button>
          </AlertDescription>
        </Alert>
      )}

      <div
        className={cn(
          'flex w-full flex-col items-center justify-center rounded border border-dashed px-6 py-10 text-center transition',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-zinc-300 bg-zinc-100',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          className="hidden"
          multiple={multiple}
          accept={acceptConfig.attr}
          onChange={handleFileInputChange}
        />

        <div>
          <div className="text-sm font-bold text-zinc-600">
            {`Drag and drop your ${multiple ? acceptConfig.nounPlural : acceptConfig.noun} here`}
          </div>
          <div className="mt-2 text-sm text-zinc-500">
            or{' '}
            <label
              htmlFor={inputId}
              className="text-primary cursor-pointer underline-offset-2 hover:underline"
            >
              browse your computer
            </label>
          </div>
          <div className="mt-2 text-xs text-zinc-400">
            {acceptConfig.label} (Max {formatFileSize(MAX_FILE_SIZE)})
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-4 w-full text-left text-xs text-zinc-600">
            <div className="mb-2 flex items-center justify-between">
              <div className="font-medium">
                Files ready to upload ({selectedFiles.length}):
              </div>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-primary hover:underline"
              >
                Clear all
              </button>
            </div>
            <ul className="mt-1 space-y-1">
              {selectedFiles.map((f, index) => (
                <li
                  key={`${f.name}-${index}`}
                  className="flex items-center justify-between rounded bg-white px-2 py-1"
                >
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showTags && selectedFiles.length > 0 && (
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">
            Tags
          </label>
          <AssetTagInput
            value={tags}
            onChange={setTags}
            placeholder={`Add tags to uploaded ${acceptConfig.nounPlural}...`}
            disabled={isPending}
          />
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            onClick={handleUpload}
            loading={isPending}
            disabled={isPending}
          >
            {multiple
              ? `Upload ${selectedFiles.length} ${fileNoun(selectedFiles.length)}`
              : `Upload ${acceptConfig.noun}`}
          </Button>
        </div>
      )}
    </div>
  );
}
